export type InvoicePreviewItemCategory = 'autoInclude' | 'review' | 'excluded';

export interface InvoicePreviewItem {
  metric: string;
  module: string;
  category: InvoicePreviewItemCategory;
  /** Pre-checked in operator UI when category === 'autoInclude'. */
  defaultSelected: boolean;
  debtUnits: number;
  coveredByFreeAllowance: number;
  coveredByPaidBalance: number;
  untrackedUnits: number;
  totalUnits: number;
  /** True when untrackedUnits > 0 — signals a mid-period config change. */
  hasUntrackedWarning: boolean;
  isBillable: boolean;
}

export interface InvoicePreviewResponse {
  projectId: string;
  periodStart: Date | string;
  periodEnd: Date | string;
  items: InvoicePreviewItem[];
}

export interface InvoiceMetricSelection {
  metric: string;
  include: boolean;
}

export interface InvoiceLineItem {
  description: string;
  module: string;
  eventType?: string;
  entityRef?: string;
  quantity: number;
  /** Quantity as measured by the metering system — locked on system lines, used for settlement. */
  originalQuantity?: number;
  unitPrice: number;
  amount: number;
  currency: string;
  isManual: boolean;
  /** True for negative-amount manual lines — grouped under Discounts & Adjustments in UI/PDF. */
  isDiscount?: boolean;
  /** Metering metric key to top up when this invoice is marked 'sent'. */
  linkedMetric?: string;
  /** Safety flag — true once the top-up credit has been applied to the bucket. */
  isTopUpProcessed?: boolean;
  /** False for lines covered by free allowance or prepaid quota (amount = 0, shown for transparency). */
  isBillable?: boolean;
  /** Human-readable note for zero-cost covered lines. */
  coveredNote?: string;
}

export interface InvoiceBillingSnapshot {
  legalName?: string;
  vatNumber?: string;
  billingAddress?: string;
  paymentTerms?: string;
  paymentMethod?: string;
  bankDetails?: string;
  invoiceRecipientEmail?: string;
  purchaseOrderRef?: string;
  projectCode?: string;
  taxRate?: number;
  currency?: string;
  language?: string;
}

export class Invoice {
  _id?: string;
  invoiceNumber?: string;
  type?: 'project' | 'distributor';
  /** Orthogonal to type. 'period' = billing period with overlap guards. 'adhoc' = manual line items only, no aggregate interaction. */
  kind?: 'period' | 'adhoc';
  project?: string;
  company?: string;
  /** Official issue date shown on the PDF. Editable while draft, frozen at confirmed. */
  invoiceDate?: Date | string;
  billingPeriodStart?: Date | string;
  billingPeriodEnd?: Date | string;
  lineItems?: InvoiceLineItem[];
  subtotal?: number;
  /** Flat discount amount applied after subtotal. */
  discountFlat?: number;
  /** Percentage discount (0–100) applied after subtotal. */
  discountPercent?: number;
  total?: number;
  currency?: string;
  status?: 'draft' | 'confirmed' | 'sent' | 'paid' | 'void';
  /** Snapshot of billing metadata captured at draft generation time. Editable before confirmation. */
  billingSnapshot?: InvoiceBillingSnapshot;
  /** True once any manual edit has been made since generation — shown as "Edited" tag in UI. */
  isEdited?: boolean;
  generatedBy?: string;
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}
